<template>
  <vx-card class="mt-base" title="Law Activity 4: 3D Virtual Tours: The Court System">
    <p class="mb-5">As in most countries, Australian courts are organised into hierarchies.</p>
    <p class="mb-5">
      A hierarchy is a structure where different levels or bodies are ranked or ordered according to
      their importance. In a court hierarchy, the various courts have different responsibilities.
    </p>
    <p class="mb-5">
      Lower courts, like the State Magistrates’ Courts, hear minor or less important cases – while
      the higher courts, like State Supreme Courts and the High Court of Australia, adjudicate more
      serious cases. Higher courts, which are also known as ‘superior courts,’ can also hear appeals
      against decisions made in lower courts.
    </p>
    <p class="mb-5">
      In Australia, both Federal and State jurisdictions have their own court hierarchies. There is
      also some sharing and crossover between the two jurisdictions to make better use of resources.
    </p>

    <centered-image-wrapper
      :image="image1"
      alt="Diagram of the Australian court hierarchy. The High Court of Australia sits at the top. Below it are the State/Territory Courts: Supreme Court, Intermediate Courts (County/District Courts) and Lower Courts (Local/Magistrates Court); and the Commonwealth Courts: Federal Court, Family Court and Federal Magistrates Court."
    />
    <p class="mb-5">
      Let’s take a look at an intermediate court, where many who work in legal professions spend a
      lot of their time.
    </p>
    <p class="mb-5">
      <strong>County Court</strong> (Victoria) or <strong>District Court</strong> (NSW, South
      Australia, Queensland, and Western Australia) hear criminal trials for serious indictable
      offences. These courts have jurisdiction to hear all but the most serious criminal charges,
      such as murder and manslaughter.
    </p>
    <p class="mb-5">
      Click on the links below to take a virtual 3D tour of Victoria County Court - District Courts
      will be laid out similarly.
    </p>
    <p class="mb-5">
      While taking the virtual tour, you can click on different parts of the image to view fact
      boxes explaining what you are looking at (you will need this information to answer the
      questions in the tasks).
    </p>

    <h4 class="mb-5">Task 1: The Foyer</h4>

    <p class="mb-5">
      Click the image below to take the virtual tour of the Foyer (it will open in a new browser tab
      so you will need to jump between the two tabs), then answer the questions below.
    </p>
    <a
      href="https://captur3d.io/view/county-court-victoria/foyer?m=imxkG5xSL7K&wh=1#"
      target="_blank"
      ><centered-image-wrapper
        alt="Virtual tour of the County Court foyer (opens in a new tab)"
        :image="image2"
    /></a>
    <p class="mb-5">
      As you take the tour of The Foyer, can you find the answers to the following questions?
    </p>

    <div v-for="(question, index) in questions1" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div
          class="vx-col w-full mb-2"
          v-for="(answer, index2) in question.answers"
          :key="index2"
          :class="{ wrong: !answer.correct && data['question' + index] == answer.val }"
          v-if="isEditable"
        >
          <vs-radio
            :vs-name="'question_1_' + index"
            v-model="data['question' + index]"
            class="checkbox_answer multiline"
            :vs-value="answer.val"
            >{{ answer.answer }}</vs-radio
          >
        </div>
        <p
          class="my-2 mx-5"
          :class="{
            wrong: !answer.correct && data['question' + index] == answer.val,
            correct: answer.correct && data['question' + index] == answer.val,
          }"
          v-else
        >
          {{ answer.answer }}
        </p>
      </div>
    </div>

    <h4 class="mb-5">Task 2: The Main Court Room</h4>

    <p class="mb-5">
      Click the image below to take the virtual tour of the Court Room, then answer the questions
      below.
    </p>
    <a
      href="https://captur3d.io/view/county-court-victoria/main-court-room?m=cS9dJWHN9fh"
      target="_blank"
      ><centered-image-wrapper
        alt="Virtual tour of the County Court main court room (opens in a new tab)"
        :image="image2"
    /></a>
    <p class="mb-5">
      As you take the tour of The Court Room, can you find the answers to the following questions?
    </p>

    <div v-for="(question, index) in questions2" :key="index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <div class="vx-row my-5">
        <div
          class="vx-col w-full mb-2"
          v-for="(answer, index2) in question.answers"
          :key="index2"
          :class="{ wrong: !answer.correct && data['question_2_' + index] == answer.val }"
          v-if="isEditable"
        >
          <vs-radio
            :vs-name="'question_2_' + index"
            v-model="data['question_2_' + index]"
            class="checkbox_answer multiline"
            :vs-value="answer.val"
            >{{ answer.answer }}</vs-radio
          >
        </div>
        <p
          class="my-2 mx-5"
          :class="{
            wrong: !answer.correct && data['question_2_' + index] == answer.val,
            correct: answer.correct && data['question_2_' + index] == answer.val,
          }"
          v-else
        >
          {{ answer.answer }}
        </p>
      </div>
    </div>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import methods from '@/views/WorkExperiences/components/activity_js/methods';
export default {
  data() {
    return {
      image1: require('@/assets/images/experiences/law/activity_41.png'),
      image2: require('@/assets/images/experiences/law/foyer_button.jpg'),
      image3: require('@/assets/images/experiences/law/courtroom_button.jpg'),
      questions1: {
        1: {
          question: 'Where do you go to seek advice about rules and processes?',
          answers: [
            { val: 1, answer: 'The Reception Desk', correct: true },
            { val: 2, answer: 'The Registry Office', correct: false },
            { val: 3, answer: 'The Jury Room', correct: false },
          ],
        },
        2: {
          question: 'Where can you find details about where and when the cases will be heard?',
          answers: [
            { val: 4, answer: 'In the Newspaper', correct: false },
            { val: 5, answer: 'At the Reception Desk', correct: false },
            { val: 6, answer: 'On the Screens', correct: true },
          ],
        },
        3: {
          question: 'What do people and property have to do before entering the Court?',
          answers: [
            { val: 7, answer: 'Register at the Reception Desk', correct: false },
            { val: 8, answer: 'Pass through Security/the weapons detection system', correct: true },
            { val: 9, answer: 'Go to the Bathroom', correct: false },
          ],
        },
      },
      questions2: {
        1: {
          question: 'What is the purpose of the television screen(s)?',
          answers: [
            {
              val: 1,
              answer: 'To allow witnesses to give evidence from a different room',
              correct: true,
            },
            {
              val: 2,
              answer: 'To provide entertainment during breaks in the proceedings',
              correct: false,
            },
            { val: 3, answer: 'To show closeups of the Defendant', correct: false },
          ],
        },
        2: {
          question: 'Who sits in the highest position in the Court Room?',
          answers: [
            { val: 4, answer: 'The Defendant', correct: false },
            { val: 5, answer: 'The Witnesses', correct: false },
            { val: 6, answer: 'The Judge', correct: true },
          ],
        },
        3: {
          question: 'Who sits in the Public Gallery?',
          answers: [
            { val: 7, answer: 'The Media', correct: false },
            { val: 8, answer: 'Anyone from the general public', correct: true },
            { val: 9, answer: 'The Jury', correct: false },
          ],
        },
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
.scale-header {
  background: #f5f5f5;
  padding-top: 5px;
  font-weight: 600;
}
</style>
